<template>
  <TheMainContent>
    <SkinnyBanner page="landing" />

    <RevContainer class="mb-16 mt-32" data-spec="body">
      <div data-spec="main">
        <div data-test="product-results-refinement">
          <div class="mb-12 justify-between lg:mb-32 lg:flex">
            <h1 class="heading-1">
              {{ i18n(translations.recapTitle) }} <span>{{ query }}</span>
            </h1>
          </div>
          <div class="sticky -top-1 z-[1]">
            <TopFilters
              v-model:filters="filtersInput"
              v-model:price="priceInput"
              v-model:sort="sortInput"
              :facets
              :is-pending
              :price-facet
              :search-nb-results="productsSearchData.total"
              :sort-options
            />
          </div>
          <div class="mt-16 lg:flex lg:gap-16">
            <div class="w-full">
              <ResultsCount
                class="md:mb-16"
                :is-pending
                :total="productsSearchData.total"
              />

              <SwapPill v-if="isBuybackEnabled" class="lg:hidden" />

              <NoResults
                v-if="productsSearchData.products.length === 0"
                class="my-32"
                data-spec="body"
              />
              <ResultGrid
                v-else
                class="mt-8 md:mt-32"
                :is-pending
                :products="productsSearchData.products"
              />
            </div>
          </div>

          <Pagination
            v-model="pageInput"
            :page-count="productsSearchData.pageCount"
          />
        </div>
      </div>
    </RevContainer>
    <FullscreenFilters
      v-model:filters="filtersInput"
      v-model:price="priceInput"
      v-model:sort="sortInput"
      :facets
      :price-facet
      :search-nb-results="productsSearchData.total"
      :sort-options
    />
  </TheMainContent>
</template>

<script lang="ts" setup>
import { createError, useHead, useRuntimeConfig } from '#imports'
import { onMounted, ref, watch } from 'vue'

import { useBuybackEnabled } from '@backmarket/nuxt-layer-buyback/app/composables/useBuybackEnabled/useBuybackEnabled'
import FullscreenFilters from '@backmarket/nuxt-layer-recommendation/filtering/FullscreenFilters.vue'
import TopFilters from '@backmarket/nuxt-layer-recommendation/filtering/TopFilters.vue'
import type {
  Facet,
  UiPriceFacet,
} from '@backmarket/nuxt-layer-recommendation/filtering/types'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevContainer } from '@ds/components/Container'

import TheMainContent from '~/components/TheMainContent/TheMainContent.vue'
import SkinnyBanner from '~/scopes/branding/components/SkinnyBanner/SkinnyBanner.vue'
import { useSkinnyBannerLoader } from '~/scopes/branding/components/SkinnyBanner/useSkinnyBanner'
import SwapPill from '~/scopes/buyback/swap/components/SwapPill/SwapPill.vue'
import {
  getSortOptions,
  useAlgoliaConfiguration,
} from '~/scopes/product-list/helpers/helpers'

import NoResults from '../../components/NoResults/NoResults.vue'
import Pagination from '../../components/Pagination/Pagination.vue'
import ResultGrid from '../../components/ResultGrid/ResultGrid.vue'
import ResultsCount from '../../components/ResultsCount/ResultsCount.vue'
import { useFiltersFromUrl } from '../../composables/useFiltersFromUrl'
import { useProductsSearch } from '../../composables/useProductsSearch/useProductsSearch'
import { useSearchInputs } from '../../composables/useSearchInputs/useSearchInputs'

import translations from './SearchResultPage.translations'

const i18n = useI18n()
const { isBuybackEnabled } = useBuybackEnabled()

const { load: loadSkinnyBannerData } = useSkinnyBannerLoader()
const [{ data: searchConfig }] = await Promise.all([
  useAlgoliaConfiguration('search-landing'),
  loadSkinnyBannerData(),
])

if (searchConfig.value === undefined) {
  throw createError({
    status: 500,
    statusText: 'Unable to retrieve search configuration',
    fatal: true,
  })
}

const sortOptions = getSortOptions(searchConfig.value.indexes)

const {
  query,
  filtersInput,
  sortInput,
  priceInput,
  pageInput,
  reset: resetSearchInputs,
  isResetting,
} = useSearchInputs(searchConfig.value)

const priceFacet = ref<UiPriceFacet | undefined>()
const facets = ref<Facet[]>([])

const { trackViewSearchResults } = useTracking()

const { data: productsSearchData, refresh: refreshProductsSearch } =
  await useProductsSearch(
    '',
    searchConfig.value,
    {
      query,
      filtersInput,
      priceInput,
      sortInput,
      pageInput,
    },
    { paused: isResetting },
  )

// @TODO: Replace this with a real loading state
const isPending = ref(false)

function setFacetsAndPriceFacet() {
  facets.value = productsSearchData.value.facets
  priceFacet.value = productsSearchData.value.priceFacet
}

setFacetsAndPriceFacet()
watch(query, async (searchTerm) => {
  resetSearchInputs()
  await refreshProductsSearch()
  trackViewSearchResults({ searchTerm })
  setFacetsAndPriceFacet()
})

useFiltersFromUrl(filtersInput, priceInput, pageInput)

const {
  public: { COMPANY_NAME },
} = useRuntimeConfig()
useHead({
  meta: [
    {
      name: 'robots',
      content: 'noindex',
    },
  ],
  title: query,
  titleTemplate: `%s | ${COMPANY_NAME}`,
})

onMounted(() => {
  trackViewSearchResults({ searchTerm: query.value })
})
</script>
